import React, { useContext, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import NotificationContext from "../context/NotificationContext";
import ExploreItemsRow from "../components/ExploreItemsRow";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useMutationLock } from "../hooks/useMutationLock";
import BulkCsvControls from "../components/BulkCsvControls";
import { WORKSHEET_UPLOAD_SAMPLE } from "../utils/csvTemplates";
import PaginationFooter from "../components/PaginationFooter";
import {
  DEFAULT_PAGE_SIZE,
  normalizeListResponse,
} from "../utils/listPagination";
import * as worksheetsApi from "../api/worksheets";
import { BULK_UPLOAD_ENDPOINT as WORKSHEET_BULK_UPLOAD_ENDPOINT } from "../api/worksheets";
import * as gradesApi from "../api/grades";
import * as subjectsApi from "../api/subjects";
import * as topicsApi from "../api/topics";

export default function AdminWorksheets() {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const notificationCtx = useContext(NotificationContext);

  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_worksheets");
  const { hasPermission: canEdit } = useViewPermission("edit_worksheets");
  const { busy, run } = useMutationLock();

  useEffect(() => {
    // permission gating handled by PermissionContext
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [topic, setTopic] = useState("");
  const [title, setTitle] = useState("");
  const [worksheetUrl, setWorksheetUrl] = useState("");
  const [solutionUrl, setSolutionUrl] = useState("");
  const [sheetDescription, setSheetDescription] = useState("");
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [gName, setGName] = useState("");
  const [sName, setSName] = useState("");
  const [tName, setTName] = useState("");

  // filter states for the right panel (sent to the API as gradeId/subjectId/topicId)
  const [filterGradeId, setFilterGradeId] = useState("");
  const [filterSubjectId, setFilterSubjectId] = useState("");
  const [filterTopicId, setFilterTopicId] = useState("");
  const [topicsForFilter, setTopicsForFilter] = useState([]);

  // fetch topic list for the RIGHT‑HAND filter panel based on selected grade/subject ids
  useEffect(() => {
    const fetchTopicsForFilter = async () => {
      try {
        // clear previous list while loading
        setTopicsForFilter([]);
        // if either id is missing we can't query
        if (!filterSubjectId || !filterGradeId) {
          return;
        }
        const res = await topicsApi.bySubjectAndGrade(
          filterSubjectId,
          filterGradeId,
          { exportMode: true },
        );
        setTopicsForFilter(normalizeListResponse(res.data).data);
      } catch (err) {
        console.error("Error fetching topics for filter:", err);
      }
    };

    fetchTopicsForFilter();
    // clear dependent topic selection when the parent filters change
    setFilterTopicId("");
  }, [filterSubjectId, filterGradeId]);

  useEffect(() => {
    setTitle((prevTitle) =>
      gName && sName && tName ? `${gName} ${sName} - ${tName}` : prevTitle,
    );
  }, [gName, sName, tName]);

  useEffect(() => {
    if (!hasPermission) return;
    const fetchGrades = async () => {
      try {
        setLoading(true);
        const response = await gradesApi.list({ exportMode: true });
        setGrades(normalizeListResponse(response.data).data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching grades:", err);
        setLoading(false);
      }
    };
    const fetchSubjects = async () => {
      try {
        setLoading(true);
        const response = await subjectsApi.list({ exportMode: true });
        setSubjects(normalizeListResponse(response.data).data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching subjects:", err);
        setLoading(false);
      }
    };

    fetchGrades();
    fetchSubjects();
  }, [hasPermission]);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        setTopics(["Loading..."]);
        if (!subject || !grade) {
          setTopics([]);
          setLoading(false);
          return;
        }
        const res = await topicsApi.bySubjectAndGrade(subject, grade, {
          exportMode: true,
        });
        setTopics(normalizeListResponse(res.data).data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching grades:", err);
        setLoading(false);
      }
    };

    fetchTopics();
  }, [subject, grade, hasPermission]);

  const limit = DEFAULT_PAGE_SIZE;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchWorksheets = async (pageArg = page) => {
    try {
      const response = await worksheetsApi.list({
        page: pageArg,
        limit,
        filters: {
          gradeId: filterGradeId,
          subjectId: filterSubjectId,
          topicId: filterTopicId,
        },
      });
      const normalized = normalizeListResponse(response.data, pageArg, limit);
      setWorksheets(normalized.data);
      setPage(normalized.page);
      setTotal(normalized.total);
      setTotalPages(normalized.totalPages);
    } catch (err) {
      console.error("Error fetching worksheets:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasPermission) return;
    setPage(1);
    fetchWorksheets(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, filterGradeId, filterSubjectId, filterTopicId]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      fetchWorksheets(newPage);
    }
  };

  const handleAddWorksheet = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    await run(async () => {
      if (!topic || !worksheetUrl || !sheetDescription) {
        notificationCtx.show({
          message: "Topic, Worksheet URL, and Description are required.",
        });
        return;
      }
      try {
      await worksheetsApi.create({
        topicId: topic,
        title,
        worksheetUrl,
        solutionUrl,
        sheetDescription,
        solutionDescription: solutionUrl
          ? `${sheetDescription} - Solution`
          : "",
      });

      setWorksheetUrl("");
      setSolutionUrl("");
      setSheetDescription("");

      setPage(1);
      await fetchWorksheets(1);

      notificationCtx.show({
        message: "Worksheet added successfully!",
        stat: "success",
      });
      } catch (err) {
        console.error("Error adding worksheet:", err);
        notificationCtx.show({
          message: err.response?.data?.message || "Failed to add worksheet.",
          stat: "error",
        });
      }
    });
  };

  const handleItemUpdate = async (itemId, { description }) => {
    if (!canEdit) return;
    await run(async () => {
      try {
        const res = await worksheetsApi.updateItem(itemId, {
          sheetDescription: description,
          solutionDescription: `${description} - Solution`,
        });
        const updatedWorksheet = res?.data;
        if (updatedWorksheet?.id) {
          setWorksheets((prev) =>
            prev.map((w) =>
              w.id === updatedWorksheet.id ? updatedWorksheet : w,
            ),
          );
        }
        notificationCtx.show({
          message: "Worksheet document description updated successfully!",
          stat: "success",
        });
      } catch (err) {
        console.error("Error updating worksheet document description:", err);
        notificationCtx.show({
          message: "Failed to update worksheet document description.",
          stat: "error",
        });
      }
    });
  };

  const handleItemDelete = (itemId) => {
    if (!canEdit) return;

    const confirmDeleteItem = async () => {
      try {
        const res = await worksheetsApi.removeItem(itemId);
        const payload = res?.data;
        if (payload?.deletedParent) {
          const nextPage =
            worksheets.length <= 1 && page > 1 ? page - 1 : page;
          setPage(nextPage);
          await fetchWorksheets(nextPage);
        } else if (payload?.id) {
          setWorksheets((prev) =>
            prev.map((w) => (w.id === payload.id ? payload : w)),
          );
        }
        notificationCtx.show({
          message: "Worksheet document removed successfully!",
          stat: "success",
        });
      } catch (err) {
        console.error("Error deleting worksheet document:", err);
        notificationCtx.show({
          message: "Failed to remove worksheet document.",
          stat: "error",
        });
      }
    };

    notificationCtx.show({
      message: "Are you sure you want to delete this worksheet document?",
      stat: "info",
      onConfirm: confirmDeleteItem,
    });
  };

  const handleDeleteWorksheet = (worksheetId) => {
    if (!canEdit) return;
    const confirmDeleteWorksheet = async () => {
      try {
        await worksheetsApi.remove(worksheetId);

        const nextPage =
          worksheets.length <= 1 && page > 1 ? page - 1 : page;
        setPage(nextPage);
        await fetchWorksheets(nextPage);

        notificationCtx.show({
          message: "Worksheet deleted successfully",
          stat: "success",
        });
      } catch (error) {
        console.error("Delete failed:", error);
        notificationCtx.show({
          message: "Failed to delete worksheet",
          stat: "error",
        });
      }
    };

    notificationCtx.show({
      message: "Are you sure you want to delete this worksheet?",
      stat: "info",
      onConfirm: confirmDeleteWorksheet,
    });
  };

  const [showWorksheetModal, setShowWorksheetModal] = useState(false);

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div
        className="d-flex flex-column"
        style={{
          minHeight: "0",
          height: "109vh",
          overflow: "hidden",
          padding: "10px 50px 0",
        }}
      >
        {/* ============================================================= */}
        {/* PAGE HEADER */}
        {/* ============================================================= */}
        <div
          style={{
            backgroundColor: "#d5d1fd",
            borderRadius: "20px",
            padding: "24px 26px",
          }}
        >
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h1
                className="mb-1"
                style={{
                  fontWeight: "700",
                  color: "#302d45",
                }}
              >
                WORKSHEETS
              </h1>

              <small
                style={{
                  color: "#625d75",
                  fontSize: "14px",
                }}
              >
                Manage worksheets, solutions, topics, and learning resources
              </small>
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap">
              <BulkCsvControls
                canEdit={canEdit}
                sampleRows={WORKSHEET_UPLOAD_SAMPLE}
                templateFilename="worksheets_upload_template.csv"
                endpoint={WORKSHEET_BULK_UPLOAD_ENDPOINT}
                notificationCtx={notificationCtx}
                onSuccess={() => {
                  setPage(1);
                  fetchWorksheets(1);
                }}
              />
              {canEdit && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => setShowWorksheetModal(true)}
                  style={{
                    height: "38px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#fff",
                    color: "#403b55",
                    border: "none",
                    padding: "0 15px",
                    borderRadius: "8px",
                    fontWeight: "600",
                    fontSize: "13px",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  + ADD WORKSHEET
                </button>
              )}
            </div>
          </div>

          {/* =========================================================== */}
          {/* FILTER / CONTROL BAR */}
          {/* =========================================================== */}
          <div className="row align-items-end">
            {/* GRADE */}
            <div className="col-md-4 p-2">
              <label
                className="form-label fw-semibold mb-2"
                style={{
                  fontSize: "13px",
                  color: "#3f3b4d",
                }}
              >
                Filter by Grade
              </label>

              <select
                className="form-select"
                value={filterGradeId}
                onChange={(e) => setFilterGradeId(e.target.value)}
                style={{
                  borderRadius: "10px",
                  border: "1px solid #d8d2ca",
                  padding: "10px 13px",
                  backgroundColor: "#ffffff",
                }}
              >
                <option value="">All Grades</option>

                {grades.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>

            {/* SUBJECT */}
            <div className="col-md-4 p-2">
              <label
                className="form-label fw-semibold mb-2"
                style={{
                  fontSize: "13px",
                  color: "#3f3b4d",
                }}
              >
                Filter by Subject
              </label>

              <select
                className="form-select"
                value={filterSubjectId}
                onChange={(e) => setFilterSubjectId(e.target.value)}
                style={{
                  borderRadius: "10px",
                  border: "1px solid #d8d2ca",
                  padding: "10px 13px",
                  backgroundColor: "#ffffff",
                }}
              >
                <option value="">All Subjects</option>

                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* TOPIC */}
            <div className="col-md-4 p-2">
              <label
                className="form-label fw-semibold mb-2"
                style={{
                  fontSize: "13px",
                  color: "#3f3b4d",
                }}
              >
                Filter by Topic
              </label>

              <select
                className="form-select"
                value={filterTopicId}
                onChange={(e) => setFilterTopicId(e.target.value)}
                disabled={!filterGradeId || !filterSubjectId}
                style={{
                  borderRadius: "10px",
                  border: "1px solid #d8d2ca",
                  padding: "10px 13px",
                  backgroundColor: "#ffffff",
                }}
              >
                <option value="">All Topics</option>

                {topicsForFilter.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.topicName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ================= SPACING ================= */}
        <div style={{ height: "16px", flexShrink: 0 }} />

        {/* ============================================================= */}
        {/* WORKSHEETS TABLE */}
        {/* ============================================================= */}
        {worksheets.length === 0 ? (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{
              minHeight: "350px",
              backgroundColor: "#ffffff",
              borderRadius: "20px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
            }}
          >
            <div
              className="d-flex align-items-center justify-content-center mb-3"
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                backgroundColor: "#f9e1be",
                fontSize: "24px",
                fontWeight: "700",
              }}
            >
              W
            </div>

            <h4 className="mb-2 fw-bold">
              {filterGradeId || filterSubjectId || filterTopicId
                ? "No worksheets found"
                : "No worksheets available"}
            </h4>

            <small
              style={{
                color: "#625d75",
              }}
            >
              {filterGradeId || filterSubjectId || filterTopicId
                ? "Try adjusting your filters."
                : "Add a worksheet to get started."}
            </small>
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              backgroundColor: "#fff",
              borderRadius: "14px",
              border: "1px solid #eeeaf8",
              boxShadow: "0 2px 10px rgba(48,45,69,0.05)",
            }}
          >
            <div
              style={{
                height: "100%",
                overflow: "auto",
              }}
            >
              <table
                style={{
                  minWidth: "1100px",
                  width: "100%",
                  tableLayout: "auto",
                  background: "#fff",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                  }}
                >
                  <tr>
                    {["title", "worksheets", "solutions"].map((key) => (
                      <th
                        key={key}
                        style={{
                          backgroundColor: "#d5d1fd",
                          color: "#000",
                          fontWeight: "700",
                          padding: "14px 16px",
                          textAlign: "center",
                          letterSpacing: "0.4px",
                          fontSize: "13px",
                          borderBottom: "1px solid #e6e6e6",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {key.toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {worksheets.map((worksheet) => (
                    <ExploreItemsRow
                      key={worksheet.id}
                      key_id={worksheet.id}
                      rowItem={worksheet}
                      onDelete={handleDeleteWorksheet}
                      onItemUpdate={handleItemUpdate}
                      onItemDelete={handleItemDelete}
                      canEdit={canEdit}
                      isWorksheet
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ============================================================= */}
        {/* PAGINATION FOOTER */}
        {/* ============================================================= */}
        {worksheets.length > 0 && (
          <PaginationFooter
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemLabel="worksheets"
            disabled={loading}
          />
        )}

        {/* ============================================================= */}
        {/* ADD WORKSHEET MODAL */}
        {/* ============================================================= */}
        {showWorksheetModal && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.55)",
              zIndex: 1055,
              padding: "20px",
            }}
            onClick={() => setShowWorksheetModal(false)}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "620px",
                maxHeight: "90vh",
                backgroundColor: "#ffffff",
                borderRadius: "20px",
                boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* MODAL HEADER */}
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: "#d5d1fd",
                  padding: "20px 24px",
                }}
              >
                <div>
                  <h3
                    className="fw-bold mb-1"
                    style={{
                      fontSize: "22px",
                    }}
                  >
                    Add Worksheet
                  </h3>

                  <small
                    style={{
                      color: "#625d75",
                    }}
                  >
                    Add a new worksheet and solution to your learning resources
                  </small>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowWorksheetModal(false)}
                  aria-label="Close"
                />
              </div>

              {/* MODAL BODY */}
              <div
                style={{
                  maxHeight: "90vh",
                  overflowY: "auto",
                  padding: "24px",
                }}
              >
                <form
                  onSubmit={(e) => {
                    handleAddWorksheet(e);
                    setShowWorksheetModal(false);
                  }}
                >
                  <div className="row">
                    {/* GRADE */}
                    <div className="col-md-6 p-2">
                      <label className="form-label fw-semibold">Grade</label>

                      <select
                        value={grade}
                        onChange={(e) => {
                          setGrade(e.target.value);
                          setGName(
                            e.target.options[e.target.selectedIndex].text,
                          );
                        }}
                        className="form-select"
                        required
                      >
                        <option value="" disabled>
                          Select Grade
                        </option>

                        {grades.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* SUBJECT */}
                    <div className="col-md-6 p-2">
                      <label className="form-label fw-semibold">Subject</label>

                      <select
                        value={subject}
                        onChange={(e) => {
                          setSubject(e.target.value);
                          setSName(
                            e.target.options[e.target.selectedIndex].text,
                          );
                        }}
                        className="form-select"
                        required
                      >
                        <option value="" disabled>
                          Select Subject
                        </option>

                        {subjects.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* TOPIC */}
                    <div className="col-md-12 p-2">
                      <label className="form-label fw-semibold">Topic</label>

                      <select
                        value={topic}
                        onChange={(e) => {
                          setTopic(e.target.value);
                          setTName(
                            e.target.options[e.target.selectedIndex].text,
                          );
                        }}
                        className="form-select"
                        required
                      >
                        <option value="" disabled>
                          Select Topic
                        </option>

                        {topics.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.topicName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* WORKSHEET URL */}
                    <div className="col-md-12 p-2">
                      <label className="form-label fw-semibold">
                        Worksheet URL
                      </label>

                      <input
                        type="text"
                        value={worksheetUrl}
                        onChange={(e) => setWorksheetUrl(e.target.value)}
                        className="form-control"
                        placeholder="Enter worksheet URL"
                        required
                      />
                    </div>

                    {/* SOLUTION URL */}
                    <div className="col-md-12 p-2">
                      <label className="form-label fw-semibold">
                        Solution URL
                      </label>

                      <input
                        type="text"
                        value={solutionUrl}
                        onChange={(e) => setSolutionUrl(e.target.value)}
                        className="form-control"
                        placeholder="Enter solution URL"
                      />
                    </div>

                    {/* DESCRIPTION */}
                    <div className="col-md-12 p-2">
                      <label className="form-label fw-semibold">
                        Worksheet Description
                      </label>

                      <textarea
                        value={sheetDescription}
                        onChange={(e) => setSheetDescription(e.target.value)}
                        className="form-control"
                        rows="4"
                        placeholder="Enter a short description for the worksheet"
                        required
                      />
                    </div>
                  </div>

                  {/* MODAL FOOTER */}
                  <div
                    className="d-flex justify-content-end gap-2 mt-4 pt-3"
                    style={{
                      borderTop: "1px solid #eeeeee",
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => setShowWorksheetModal(false)}
                      style={{
                        fontWeight: "600",
                        borderRadius: "10px",
                        padding: "9px 18px",
                      }}
                    >
                      CANCEL
                    </button>

                    <button
                      type="submit"
                      className="btn"
                      style={{
                        backgroundColor: "#f2c4c4",
                        borderColor: "#f2c4c4",
                        color: "#000",
                        fontWeight: "700",
                        borderRadius: "10px",
                        padding: "9px 20px",
                      }}
                      disabled={
                        busy ||
                        !grade ||
                        !subject ||
                        !topic ||
                        !worksheetUrl ||
                        !sheetDescription
                      }
                    >
                      {busy ? "Working..." : "ADD WORKSHEET"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
